/* AI Snapshot — camera modal for the Interactive Board.
 *
 * Redesigned workflow:
 *   1. Capture paper work with the camera
 *   2. It is SAVED to the cloud gallery first (never lost, zero AI cost)
 *   3. Analysis (OCR + grading) is optional — run now, or reopen any saved
 *      snapshot later; results are cached on the asset so reopening is free.
 * Teachers can also review incoming student snapshots and place them on the
 * board to annotate mistakes.
 */
import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  X, Camera, RefreshCcw, Loader2, FileText, ClipboardCheck, CloudUpload,
  Image as ImageIcon, CheckCircle2, XCircle, AlertTriangle, PlusSquare, ScanLine, Images,
} from "lucide-react";

export default function SnapshotModal({ open, onClose, classId, isTeacher, review = null, onPlaceOnBoard, startMode = "camera" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [mode, setMode] = useState(startMode); // camera | gallery
  const [captured, setCaptured] = useState(null);
  const [busy, setBusy] = useState(null); // "save" | "analyze" | "reanalyze"
  const [result, setResult] = useState(review);
  const [tab, setTab] = useState(review ? "results" : "camera");
  const [camError, setCamError] = useState("");
  const [roster, setRoster] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  useEffect(() => {
    if (!open || !isTeacher || !classId || review) return;
    (async () => {
      try {
        const { data } = await api.get(`/live/${classId}/roster`);
        const students = data.students || [];
        setRoster(students);
        const suggested =
          students.find((s) => (data.remote_access_ids || []).includes(s.id))?.id ||
          (students.length === 1 ? students[0].id : "");
        setStudentId(suggested);
      } catch { /* roster optional */ }
    })();
  }, [open, isTeacher, classId, review]);

  const stopCam = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    if (!open || review || result || mode !== "camera") { if (mode !== "camera") stopCam(); return; }
    let alive = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 2560 }, height: { ideal: 1440 } }, audio: false,
        });
        if (!alive) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch {
        setCamError("Camera unavailable — allow camera access in your browser and try again.");
      }
    })();
    return () => { alive = false; stopCam(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, review, !!result, mode]);

  const loadGallery = async () => {
    setGalleryLoading(true);
    try {
      const { data } = await api.get("/gallery", { params: { kind: "snapshot" } });
      setGalleryItems(data.assets || []);
    } catch { /* silent */ }
    finally { setGalleryLoading(false); }
  };
  useEffect(() => { if (open && mode === "gallery" && isTeacher) loadGallery(); /* eslint-disable-next-line */ }, [open, mode]);

  if (!open) return null;

  const capture = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { toast.error("Camera not ready yet"); return; }
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setCaptured(c.toDataURL("image/jpeg", 0.85));
  };

  const save = async (withAnalysis) => {
    if (!captured || busy) return;
    setBusy(withAnalysis ? "analyze" : "save");
    try {
      const { data } = await api.post("/gallery/snapshot", {
        image_data_url: captured,
        class_id: classId || "",
        ...(isTeacher && studentId ? { student_id: studentId } : {}),
      });
      const asset = data.asset;
      stopCam();
      if (!withAnalysis) {
        toast.success("Saved to your cloud gallery");
        setCaptured(null);
        if (isTeacher) { setMode("gallery"); }
        else { onClose?.(); }
        return;
      }
      const { data: an } = await api.post(`/gallery/${asset.id}/analyze`, {});
      setResult({
        asset_id: asset.id,
        image_url: asset.url,
        transcript: an.transcript,
        result: an.analysis,
        by_name: asset.by_name,
        student_name: data.snapshot?.student_name || asset.student_name || "",
        created_at: asset.created_at,
      });
      setTab("transcript");
      toast.success(isTeacher ? "Saved to gallery & analyzed" : "Snapshot sent — your teacher sees it now");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save snapshot");
    } finally { setBusy(null); }
  };

  const openAsset = (a) => {
    setResult({
      asset_id: a.id,
      image_url: a.url,
      transcript: a.transcript || "",
      result: a.analysis || null,
      by_name: a.by_name,
      student_name: a.student_name || "",
      created_at: a.created_at,
    });
    setTab(a.analysis ? "results" : "photo");
  };

  const analyzeExisting = async () => {
    const aid = result?.asset_id || review?.asset_id;
    if (!aid || busy) return;
    setBusy("reanalyze");
    try {
      const { data } = await api.post(`/gallery/${aid}/analyze`, {});
      setResult((cur) => ({ ...cur, transcript: data.transcript, result: data.analysis }));
      setTab("results");
      toast.success(data.cached ? "Loaded cached analysis" : "Analysis ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI analysis failed");
    } finally { setBusy(null); }
  };

  const doc = result;
  const verdict = doc?.result?.correct;
  const analyzing = busy === "analyze";

  return (
    <div className="fixed inset-0 z-[96] bg-black/80 backdrop-blur-md flex items-center justify-center px-4" data-testid="ai-snapshot-modal" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <motion.div
        initial={{ scale: 0.94, y: 14, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }}
        className="w-full max-w-5xl bg-[#0B0F17] border border-[#22D3EE]/30 rounded-2xl shadow-[0_0_80px_-20px_rgba(34,211,238,0.6)] overflow-hidden text-white"
      >
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#22D3EE]/10 to-transparent">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#22D3EE] font-bold inline-flex items-center gap-1.5">
              <ScanLine size={11} /> / AI snapshot
            </div>
            <div className="font-display font-bold text-lg tracking-tight mt-0.5">
              {doc ? (review ? `${doc.by_name}'s work` : (doc.result ? "Analysis ready" : "Saved snapshot")) : mode === "gallery" ? "Your snapshot gallery" : "Show your work to the camera"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!doc && isTeacher && (
              <div className="flex rounded-md border border-white/15 overflow-hidden text-[11px] font-semibold" data-testid="snapshot-mode-tabs">
                <button onClick={() => setMode("camera")} data-testid="snapshot-mode-camera"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${mode === "camera" ? "bg-[#22D3EE] text-[#062A3A]" : "text-white/60 hover:text-white"}`}>
                  <Camera size={11} /> Camera
                </button>
                <button onClick={() => setMode("gallery")} data-testid="snapshot-mode-gallery"
                  className={`px-3 py-1.5 inline-flex items-center gap-1.5 ${mode === "gallery" ? "bg-[#22D3EE] text-[#062A3A]" : "text-white/60 hover:text-white"}`}>
                  <Images size={11} /> Gallery
                </button>
              </div>
            )}
            <button onClick={onClose} disabled={!!busy} data-testid="snapshot-close" className="text-white/60 hover:text-white p-1 rounded hover:bg-white/10 disabled:opacity-40"><X size={16} /></button>
          </div>
        </div>

        {!doc && mode === "gallery" ? (
          /* ── GALLERY STAGE — reopen saved snapshots ── */
          <div className="p-5 max-h-[62vh] overflow-y-auto" data-testid="snapshot-gallery-stage">
            {galleryLoading ? (
              <div className="py-12 text-center text-sm text-white/50 inline-flex w-full justify-center items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Loading snapshots…
              </div>
            ) : galleryItems.length === 0 ? (
              <div className="py-12 text-center text-sm text-white/50">No snapshots saved yet — capture one with the camera.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {galleryItems.map((a) => (
                  <button key={a.id} onClick={() => openAsset(a)} data-testid={`snapshot-gallery-item-${a.id}`}
                    className="group text-left border border-white/10 hover:border-[#22D3EE] rounded-lg overflow-hidden bg-black/40 transition-colors">
                    <div className="aspect-square bg-black/60">
                      <img src={a.url} alt={a.title} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="px-2 py-1.5">
                      <div className="text-[11px] font-medium truncate">{a.title}</div>
                      <div className="text-[9px] font-mono uppercase tracking-wider mt-0.5 inline-flex items-center gap-1">
                        {a.analysis
                          ? <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 size={9} /> Analyzed</span>
                          : <span className="text-white/40">Not analyzed</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : !doc ? (
          /* ── CAMERA STAGE ── */
          <div className="p-5">
            <div className="relative rounded-xl overflow-hidden bg-black h-[48vh] md:h-[56vh] border border-[#22D3EE]/25" data-testid="snapshot-camera-stage">
              {camError ? (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center p-6">
                  <AlertTriangle size={22} className="text-amber-400" />
                  <div className="text-sm text-white/70">{camError}</div>
                </div>
              ) : captured ? (
                <img src={captured} alt="captured work" className="w-full h-full object-contain" data-testid="snapshot-captured-preview" />
              ) : (
                <>
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-contain" />
                  <div className="absolute inset-6 pointer-events-none">
                    {["top-0 left-0 border-t-2 border-l-2", "top-0 right-0 border-t-2 border-r-2", "bottom-0 left-0 border-b-2 border-l-2", "bottom-0 right-0 border-b-2 border-r-2"].map((c) => (
                      <div key={c} className={`absolute w-10 h-10 ${c} border-[#22D3EE]`} />
                    ))}
                    <motion.div
                      className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-[#22D3EE] to-transparent"
                      animate={{ top: ["4%", "94%", "4%"] }} transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
                    />
                  </div>
                  <div className="absolute bottom-3 inset-x-0 text-center text-[10px] font-mono uppercase tracking-[0.24em] text-[#22D3EE]/90">
                    Hold the paper flat · fill the frame · keep steady
                  </div>
                </>
              )}
              {analyzing && (
                <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3" data-testid="snapshot-analyzing">
                  <div className="relative w-20 h-20">
                    <div className="absolute inset-0 rounded-full border-2 border-[#22D3EE]/40 animate-ping" />
                    <div className="absolute inset-3 rounded-full border-2 border-[#22D3EE]/70 animate-ping" style={{ animationDelay: "0.3s" }} />
                    <div className="absolute inset-6 rounded-full bg-[#22D3EE] animate-pulse" />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/80">Saving & reading your work…</div>
                  <div className="text-[10px] font-mono text-white/40">cloud save → transcribe → check · 10–20s</div>
                </div>
              )}
            </div>
            {isTeacher && roster.length > 0 && (
              <div className="mt-4 flex items-center justify-center gap-2 flex-wrap" data-testid="snapshot-student-picker">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Save to profile:</span>
                <select
                  value={studentId}
                  onChange={(e) => setStudentId(e.target.value)}
                  data-testid="snapshot-student-select"
                  className="bg-white/[0.06] border border-[#22D3EE]/30 rounded-md px-3 py-1.5 text-xs text-white focus:outline-none focus:border-[#22D3EE] [&>option]:text-black"
                >
                  <option value="">Don't save to a profile</option>
                  {roster.map((s) => (
                    <option key={s.id} value={s.id}>{s.full_name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
              {!captured ? (
                <button onClick={capture} disabled={!!camError} data-testid="snapshot-capture-btn"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#22D3EE] text-[#062A3A] font-bold text-sm hover:brightness-110 disabled:opacity-40 transition">
                  <Camera size={15} /> Capture
                </button>
              ) : (
                <>
                  <button onClick={() => setCaptured(null)} disabled={!!busy} data-testid="snapshot-retake-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm hover:bg-white/10 disabled:opacity-40">
                    <RefreshCcw size={14} /> Retake
                  </button>
                  <button onClick={() => save(false)} disabled={!!busy} data-testid="snapshot-save-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#22D3EE]/50 text-[#22D3EE] text-sm font-semibold hover:bg-[#22D3EE]/10 disabled:opacity-40">
                    {busy === "save" ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={14} />} Save to gallery
                  </button>
                  <button onClick={() => save(true)} disabled={!!busy} data-testid="snapshot-analyze-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#22D3EE] to-[#7C3AED] text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 transition">
                    {analyzing ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={15} />} Save &amp; analyze
                  </button>
                </>
              )}
            </div>
          </div>
        ) : (
          /* ── RESULT TABS ── */
          <div>
            <div className="flex border-b border-white/10" data-testid="snapshot-tabs">
              {[["photo", "Photo", ImageIcon], ["transcript", "Transcription", FileText], ["results", "AI Results", ClipboardCheck]].map(([id, label, Icon]) => (
                <button key={id} onClick={() => setTab(id)} data-testid={`snapshot-tab-${id}`}
                  className={`flex-1 py-3 text-xs font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${tab === id ? "text-[#22D3EE] border-b-2 border-[#22D3EE] bg-[#22D3EE]/5" : "text-white/50 hover:text-white"}`}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            <div className="p-5 max-h-[52vh] overflow-y-auto">
              {tab === "photo" && (
                <img src={doc.image_url} alt="student work" className="w-full rounded-lg border border-white/10 object-contain max-h-[46vh]" data-testid="snapshot-photo" />
              )}
              {tab === "transcript" && (
                doc.result || doc.transcript ? (
                  <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap font-mono text-white/90" data-testid="snapshot-transcript">
                    {doc.transcript || "No readable text detected — try a clearer, closer shot."}
                  </div>
                ) : (
                  <NotAnalyzed onAnalyze={analyzeExisting} busy={busy === "reanalyze"} />
                )
              )}
              {tab === "results" && (
                !doc.result ? (
                  <NotAnalyzed onAnalyze={analyzeExisting} busy={busy === "reanalyze"} />
                ) : (
                <div className="space-y-4" data-testid="snapshot-results">
                  <div className="flex items-center gap-3 flex-wrap">
                    {verdict === true && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-400 text-xs font-bold"><CheckCircle2 size={13} /> PASS</span>
                    )}
                    {verdict === false && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-500/15 border border-red-400/40 text-red-300 text-xs font-bold"><XCircle size={13} /> NEEDS WORK</span>
                    )}
                    {verdict == null && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-300 text-xs font-bold"><AlertTriangle size={13} /> REVIEW</span>
                    )}
                    {doc.result?.score != null && (
                      <span className="text-2xl font-display font-bold">{doc.result.score}<span className="text-white/40 text-sm">/100</span></span>
                    )}
                  </div>
                  {doc.result?.feedback && (
                    <div className="bg-white/[0.04] border border-white/10 rounded-lg p-4 text-sm leading-relaxed">{doc.result.feedback}</div>
                  )}
                  {(doc.result?.mistakes || []).length > 0 && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-red-300 mb-2">Mistakes spotted</div>
                      <ul className="space-y-1.5">
                        {doc.result.mistakes.map((m, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-white/85">
                            <XCircle size={13} className="text-red-400 mt-0.5 shrink-0" /> {typeof m === "string" ? m : JSON.stringify(m)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {doc.result?.improvement_plan && (
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#22D3EE] mb-2">Suggestions</div>
                      <div className="text-sm text-white/80 leading-relaxed">{doc.result.improvement_plan}</div>
                    </div>
                  )}
                </div>
                )
              )}
            </div>
            <div className="px-5 py-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-2">
              <span className="text-[10px] font-mono uppercase tracking-wider text-white/40">
                {doc.by_name} · {doc.created_at ? new Date(doc.created_at).toLocaleTimeString() : ""}
                {doc.student_name ? <span className="text-[#22D3EE]"> · saved to {doc.student_name}'s profile</span> : null}
                <span className="text-emerald-400/70"> · in cloud gallery</span>
              </span>
              <div className="flex items-center gap-2">
                {!review && isTeacher && (
                  <button onClick={() => { setResult(null); setMode("gallery"); }} data-testid="snapshot-back-gallery"
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-white/20 text-xs text-white/70 hover:bg-white/10">
                    <Images size={12} /> Gallery
                  </button>
                )}
                {isTeacher && onPlaceOnBoard && (
                  <button onClick={() => onPlaceOnBoard(doc.image_url)} data-testid="snapshot-place-on-board"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#FFD600] text-[#0A0C10] text-xs font-bold hover:bg-[#FFC600]">
                    <PlusSquare size={13} /> Place on board
                  </button>
                )}
                <button onClick={onClose} className="px-4 py-2 rounded-md border border-white/20 text-xs text-white/80 hover:bg-white/10">Done</button>
              </div>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function NotAnalyzed({ onAnalyze, busy }) {
  return (
    <div className="py-10 text-center" data-testid="snapshot-not-analyzed">
      <div className="text-sm text-white/60 mb-4">This snapshot hasn't been analyzed yet — it's safely stored in your cloud gallery.</div>
      <button onClick={onAnalyze} disabled={busy} data-testid="snapshot-run-analysis"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#22D3EE] to-[#7C3AED] text-white font-bold text-sm hover:brightness-110 disabled:opacity-60 transition">
        {busy ? <Loader2 size={14} className="animate-spin" /> : <ScanLine size={15} />} Run OCR + AI analysis
      </button>
    </div>
  );
}
